import { useEffect, useState } from 'react'
import { Link, getRouteApi } from '@tanstack/react-router'
import { BadgeCheck, Briefcase, Building2, Clock, MapPin, Search, Users } from 'lucide-react'

import { PageIntro } from '@/components/page-intro'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Select } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { arrangementLabel, employmentLabel, experienceLabel } from '@/components/ui/status'
import { salaryLabel, useVacancies } from '@/features/hiring/api'
import { useMarketplaceCategories } from '@/features/marketplace/api'
import { formatNumber, formatRelative, formatRupiahShort } from '@/lib/format'
import type { Vacancy } from '@/lib/types'

const route = getRouteApi('/public/lowongan')

const employmentOptions = [
  { value: '', label: 'Semua jenis' },
  { value: 'full_time', label: 'Penuh waktu' },
  { value: 'part_time', label: 'Paruh waktu' },
  { value: 'contract', label: 'Kontrak' },
  { value: 'internship', label: 'Magang' },
  { value: 'freelance', label: 'Lepas' },
]

const arrangementOptions = [
  { value: '', label: 'Semua lokasi kerja' },
  { value: 'onsite', label: 'Di kantor' },
  { value: 'hybrid', label: 'Hibrida' },
  { value: 'remote', label: 'Jarak jauh' },
]

const levelOptions = [
  { value: '', label: 'Semua tingkat' },
  { value: 'entry', label: 'Pemula' },
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Menengah' },
  { value: 'senior', label: 'Senior' },
  { value: 'lead', label: 'Lead' },
]

export function VacanciesPage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const [draft, setDraft] = useState(search.q ?? '')

  useEffect(() => {
    setDraft(search.q ?? '')
  }, [search.q])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if ((search.q ?? '') === draft) return
      navigate({ search: (previous) => ({ ...previous, q: draft || undefined, page: 1 }), replace: true })
    }, 350)
    return () => window.clearTimeout(timer)
  }, [draft, navigate, search.q])

  const { data, isLoading } = useVacancies({
    q: search.q,
    category: search.category,
    employment_type: search.employment_type,
    work_arrangement: search.work_arrangement,
    experience_level: search.experience_level,
    city: search.city,
    page: search.page,
  })
  const { data: categories } = useMarketplaceCategories()

  const vacancies = data?.vacancies ?? []
  const meta = data?.meta

  const setSearch = (patch: Record<string, string | undefined>) => {
    navigate({ search: (previous) => ({ ...previous, ...patch, page: 1 }) })
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
      <PageIntro
        accent="lowongan"
        eyebrow="Lowongan"
        icon={<Building2 className="size-3.5" />}
        title="Lowongan kerja"
        description="Posisi tetap, kontrak, magang, dan pekerjaan lepas dari perusahaan yang profilnya terbuka."
      />

      <div className="mt-8 grid gap-3 lg:grid-cols-[2fr_1fr_1fr_1fr]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-subtle" />
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Cari posisi, kata kunci, atau perusahaan"
            aria-label="Cari lowongan"
            className="pl-10"
          />
        </div>

        <Select
          value={search.employment_type ?? ''}
          onChange={(event) => setSearch({ employment_type: event.target.value || undefined })}
          aria-label="Jenis pekerjaan"
        >
          {employmentOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>

        <Select
          value={search.work_arrangement ?? ''}
          onChange={(event) => setSearch({ work_arrangement: event.target.value || undefined })}
          aria-label="Lokasi kerja"
        >
          {arrangementOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>

        <Select
          value={search.experience_level ?? ''}
          onChange={(event) => setSearch({ experience_level: event.target.value || undefined })}
          aria-label="Tingkat pengalaman"
        >
          {levelOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </div>

      {categories?.length ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <CategoryChip active={!search.category} onClick={() => setSearch({ category: undefined })}>
            Semua bidang
          </CategoryChip>
          {categories.slice(0, 10).map((category) => (
            <CategoryChip
              key={category.slug}
              active={search.category === category.slug}
              onClick={() => setSearch({ category: category.slug })}
            >
              {category.name}
            </CategoryChip>
          ))}
        </div>
      ) : null}

      <p className="mt-6 border-b border-line pb-4 text-sm text-muted" aria-live="polite">
        {isLoading ? 'Memuat lowongan…' : `${formatNumber(meta?.total ?? 0)} lowongan terbuka`}
      </p>

      {isLoading ? (
        <div className="mt-6 space-y-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-36 rounded-2xl" />
          ))}
        </div>
      ) : vacancies.length === 0 ? (
        <Card className="mt-6">
          <EmptyState illustration="box"
            icon={<Briefcase className="size-6" />}
            title="Tidak ada lowongan yang cocok"
            description="Coba longgarkan penyaring, atau simpan halaman ini dan periksa kembali nanti."
            action={
              <Button variant="outline" onClick={() => navigate({ search: {} })}>
                Atur ulang pencarian
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="mt-6 space-y-3">
          {vacancies.map((vacancy) => (
            <VacancyRow key={vacancy.id} vacancy={vacancy} />
          ))}
        </div>
      )}

      {meta && meta.total_pages > 1 ? (
        <nav className="mt-8 flex items-center justify-center gap-2" aria-label="Navigasi halaman">
          <Button
            variant="outline"
            size="sm"
            disabled={meta.page <= 1}
            onClick={() => navigate({ search: (previous) => ({ ...previous, page: meta.page - 1 }) })}
          >
            Sebelumnya
          </Button>
          <span className="px-3 text-sm text-muted">
            Halaman {meta.page} dari {meta.total_pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={meta.page >= meta.total_pages}
            onClick={() => navigate({ search: (previous) => ({ ...previous, page: meta.page + 1 }) })}
          >
            Berikutnya
          </Button>
        </nav>
      ) : null}
    </div>
  )
}

function CategoryChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition ${
        active ? 'border-brand bg-brand-soft text-brand-ink' : 'border-line text-muted hover:text-ink'
      }`}
    >
      {children}
    </button>
  )
}

/** Baris lowongan pada papan; dipakai juga oleh daftar lowongan tersimpan. */
export function VacancyRow({ vacancy }: { vacancy: Vacancy }) {
  return (
    <Card className="transition hover:border-brand/40">
      <CardBody className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 gap-4">
          {vacancy.company.logo_url ? (
            <img
              src={vacancy.company.logo_url}
              alt=""
              className="size-12 shrink-0 rounded-xl border border-line object-cover"
            />
          ) : (
            <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand">
              <Building2 className="size-6" />
            </span>
          )}

          <div className="min-w-0">
            <Link to="/lowongan/$vacancyId" params={{ vacancyId: vacancy.id }}>
              <h2 className="text-lg font-bold text-ink transition hover:text-brand">{vacancy.title}</h2>
            </Link>

            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-muted">
              <span className="inline-flex items-center gap-1 font-medium text-ink">
                {vacancy.company.name}
                {vacancy.company.verified ? <BadgeCheck className="size-3.5 text-brand" /> : null}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <MapPin className="size-3.5" />
                {vacancy.city || arrangementLabel[vacancy.work_arrangement]}
              </span>
            </p>

            <div className="mt-3 flex flex-wrap gap-1.5">
              <Badge tone="brand">{employmentLabel[vacancy.employment_type]}</Badge>
              <Badge>{arrangementLabel[vacancy.work_arrangement]}</Badge>
              <Badge>{experienceLabel[vacancy.experience_level]}</Badge>
              {vacancy.skills.slice(0, 3).map((skill) => (
                <Badge key={skill}>{skill}</Badge>
              ))}
            </div>
          </div>
        </div>

        <div className="shrink-0 text-right">
          {vacancy.salary_visible && vacancy.salary_max ? (
            <>
              <p className="text-xs text-subtle">{salaryLabel(vacancy.employment_type)}</p>
              <p className="text-base font-extrabold tabular-nums text-ink">
                {formatRupiahShort(vacancy.salary_min ?? 0)} – {formatRupiahShort(vacancy.salary_max)}
              </p>
            </>
          ) : (
            // Perusahaan yang belum membuka angka gaji dikatakan apa adanya,
            // bukan disamarkan dengan tanda hubung yang membingungkan.
            <p className="text-sm text-muted">Gaji dirundingkan</p>
          )}

          <div className="mt-2 flex items-center justify-end gap-3 text-xs text-subtle">
            <span className="inline-flex items-center gap-1">
              <Users className="size-3.5" />
              {vacancy.application_count} pelamar
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3.5" />
              {formatRelative(vacancy.published_at ?? vacancy.created_at)}
            </span>
          </div>

          <Link to="/lowongan/$vacancyId" params={{ vacancyId: vacancy.id }} className="mt-3 inline-block">
            <Button size="sm">{vacancy.my_application_state ? 'Lihat lamaran' : 'Lihat & lamar'}</Button>
          </Link>
        </div>
      </CardBody>
    </Card>
  )
}
